import { EuiButton, EuiSearchBar } from '@elastic/eui'
import { useAppDispatch, useAppSelector, useClientBuildings } from 'modules/module-utils'
import { Grid, useToggle, useTranslate } from 'modules/web-atoms'
import { DeleteConfirmationModal, Table } from 'modules/web-molecules'
import { IRadioSelectorItem } from 'modules/web-organisms'
import { ICreateOptions, IDeleteOptions, ISearchOptions } from 'modules/web-templates'
import React from 'react'
import { useCouriersTable } from '../../../hooks/use-couriers-table/use-couriers-table'
import settings, { SettingsFormState } from '../../../settings'

const TABLE_ID = 'table-couriers'
const INTL_PREFIX_MODAL = 'settings.tabs.couriers.delete-modal'

const CouriersTable: React.FC = () => {
  const t = useTranslate('settings.tabs.couriers.table')
  const dispatch = useAppDispatch()

  const [isDeleteModalOpen, toggleDeleteModal] = useToggle(false)

  const selectedItems = useAppSelector(settings.selectors.selectedCouriers)
  const selectedItem = useAppSelector(settings.selectors.selectedCourier)
  const courierFormState = useAppSelector(settings.selectors.courierFormState)
  const selectedSite = useAppSelector(settings.selectors.selectedSite)
  const isAdding = useAppSelector(settings.selectors.isAddingCourier)
  const isDeleting = useAppSelector(settings.selectors.isDeletingCouriers)
  const { canManageCouriers } = useAppSelector(settings.selectors.permissions)

  const { buildings, isFetching: isFetchingBuildings } = useClientBuildings()
  const siteOptions: IRadioSelectorItem[] = React.useMemo(
    () =>
      buildings?.map((building) => ({
        id: building.id.toString(),
        label: building.buildingName,
      })) ?? [],
    [buildings],
  )

  const {
    columns,
    items,
    selection,
    onItemClicked,
    isFetching,
    sort,
    onSearch,
    error,
    pagination,
    paginationOptions,
    selectedCouriers,
  } = useCouriersTable(selectedSite)
  const isLoading = isFetching || isFetchingBuildings

  React.useEffect(() => {
    if (siteOptions && siteOptions.length) {
      dispatch(settings.actions.setSelectedSite(parseInt(siteOptions[0].id)))
    }
  }, [siteOptions, dispatch])

  const onAddClick = React.useCallback(() => {
    dispatch(settings.actions.setCourierFormState(SettingsFormState.CREATE))
  }, [dispatch])

  const createOptions: ICreateOptions = React.useMemo(
    () => ({
      visible: canManageCouriers && courierFormState !== SettingsFormState.CREATE,
      onClick: onAddClick,
      value: t('add'),
    }),
    [courierFormState, canManageCouriers, onAddClick, t],
  )

  const onDelete = React.useCallback(() => {
    dispatch(settings.actions.deleteCouriers())
    toggleDeleteModal()
  }, [dispatch, toggleDeleteModal])

  const searchOptions: ISearchOptions = React.useMemo(
    () => ({
      hint: t('search.placeholder'),
      onSearch,
    }),
    [t, onSearch],
  )

  const deleteOptions: IDeleteOptions = React.useMemo(
    () => ({
      visible: (selectedItems.length > 0 || !!selectedItem) && canManageCouriers,
      isLoading: isDeleting,
      onClick: onDelete,
      value: t('delete', { count: selectedItems.length || 1 }),
    }),
    [selectedItems.length, selectedItem, isDeleting, onDelete, canManageCouriers, t],
  )

  const onQuery = React.useCallback(
    ({ queryText: filter }) => {
      searchOptions?.onSearch(filter)
    },
    [searchOptions],
  )

  return (
    <Grid flexGrow={1} minHeight={0} gutterSize="small">
      <Grid flexWrap="wrap" gutterSize="small" justifyContent="space-between">
        <Grid.Col>
          <Grid>
            <Grid flexWrap="wrap">
              {deleteOptions.visible && (
                <Grid.Col>
                  <EuiButton color="danger" fill onClick={toggleDeleteModal} isLoading={deleteOptions.isLoading}>
                    {deleteOptions.value}
                  </EuiButton>
                </Grid.Col>
              )}
            </Grid>
          </Grid>
        </Grid.Col>

        <Grid.Col alignSelf={['flex-start', 'flex-end']}>
          <Grid gutterSize={['smallest', 'medium']} flexWrap="wrap">
            <Grid.Col>
              <EuiSearchBar box={{ placeholder: searchOptions.hint, incremental: true }} onChange={onQuery} />
            </Grid.Col>
            {createOptions.visible && (
              <EuiButton
                onClick={createOptions.onClick}
                color="primary"
                fill
                isDisabled={!selectedSite}
                isLoading={isAdding}
              >
                {createOptions.value}
              </EuiButton>
            )}
          </Grid>
        </Grid.Col>
      </Grid>

      <Table
        tableId={TABLE_ID}
        columns={columns}
        items={items}
        selection={selection}
        sorting={sort}
        pagination={pagination}
        paginationOptions={paginationOptions}
        loading={isLoading}
        isCheckable={canManageCouriers}
        onItemClicked={canManageCouriers ? onItemClicked : undefined}
        selectedItems={selectedCouriers}
      />

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          onClose={toggleDeleteModal}
          onDelete={onDelete}
          itemSize={selectedItems.length || 1}
          intlPrefix={INTL_PREFIX_MODAL}
        />
      )}
    </Grid>
  )
}

export default React.memo(CouriersTable)
